/**
 * ColorPropertyEditor
 *
 * Reusable color input component for property editors
 * SRP: Responsible ONLY for rendering a color picker/swatch and handling changes
 * Used by: backgroundColor, color, borderColor, shadowColor, etc.
 */

import React, { useCallback, useMemo } from 'react';

export interface ColorPropertyEditorProps {
  /** Display label for the input */
  label: string;

  /** Current value (#RRGGBB format) */
  value: string;

  /** Called when value changes */
  onChange: (value: string) => void;

  /** Field is required */
  required?: boolean;

  /** Field is disabled */
  disabled?: boolean;

  /** Help text shown below input */
  helpText?: string;

  /** Show RGB input fields */
  showRgb?: boolean;

  /** Custom CSS class */
  className?: string;
}

/**
 * ColorPropertyEditor component
 * Renders a color swatch with hex input field
 */
export const ColorPropertyEditor: React.FC<ColorPropertyEditorProps> = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  helpText,
  showRgb = false,
  className = '',
}) => {
  // Memoize handler to prevent unnecessary re-renders
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.currentTarget.value;
      // Validate hex color format
      if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(newValue) || newValue === '') {
        onChange(newValue);
      }
    },
    [onChange],
  );

  // Handle color picker input
  const handleColorPickerChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.currentTarget.value);
    },
    [onChange],
  );

  const inputId = useMemo(() => `color-editor-${label.replace(/\s+/g, '-').toLowerCase()}`, [label]);

  // Parse hex to RGB for display
  const parseHexToRgb = useMemo(() => {
    if (!value || !value.startsWith('#')) return null;
    const hex = value.slice(1);
    if (hex.length === 6) {
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return { r, g, b };
    }
    return null;
  }, [value]);

  const isValidColor = useMemo(
    () => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value),
    [value],
  );

  return (
    <div className={`af-property-editor__group ${className}`}>
      <label htmlFor={inputId} className="af-property-editor__label">
        {label}
        {required && <span className="af-property-editor__required">*</span>}
      </label>

      <div className="af-property-editor__color-wrapper">
        <input
          type="color"
          value={isValidColor ? value : '#000000'}
          onChange={handleColorPickerChange}
          disabled={disabled}
          className="af-property-editor__color-picker"
          aria-label={`${label} color picker`}
        />

        <input
          id={inputId}
          type="text"
          value={value}
          onChange={handleChange}
          placeholder="#RRGGBB"
          disabled={disabled}
          required={required}
          className={`af-property-editor__input af-property-editor__input--hex ${
            !isValidColor && value ? 'af-property-editor__input--error' : ''
          }`}
          aria-label={label}
          aria-required={required}
          aria-invalid={!isValidColor && value !== ''}
          aria-describedby={helpText ? `${inputId}-help` : undefined}
          maxLength={7}
        />
      </div>

      {showRgb && parseHexToRgb && (
        <div className="af-property-editor__rgb-values">
          <span className="af-property-editor__rgb-value">
            R: {parseHexToRgb.r}
          </span>
          <span className="af-property-editor__rgb-value">
            G: {parseHexToRgb.g}
          </span>
          <span className="af-property-editor__rgb-value">
            B: {parseHexToRgb.b}
          </span>
        </div>
      )}

      {!isValidColor && value && (
        <div className="af-property-editor__error">
          Invalid color format. Use #RRGGBB or #RGB
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-property-editor__help">
          {helpText}
        </div>
      )}
    </div>
  );
};

ColorPropertyEditor.displayName = 'ColorPropertyEditor';
